<template>
  <div class="typeahead">
    <input-widget
        :model="model" :attribute="attribute" :rules="rules" :disabled="disabled" @keyup="onKeyup"
        @input="getPostCodeInfo" @blur="onBlur" @mousedown="onMousedown" @keydown.down="onArrowKeyDown"
        @keydown.up="onArrowKeyUp" @keydown.esc="resetInfo" @keydown.enter="getAddressInfo">
    </input-widget>
    <div class="results-ctr" tabindex="0" @mousedown="isFocused=true" @blur="isFocused=false">
      <ul class="result-list shadow" v-show="isOpen">
        <li v-for="(item, index) in items" class="py-1 px-3" :class="{'active': index === activeIndex}" :key="item.id"
            @mousemove="setActive(index)" @mousedown="getAddressInfo">
          {{ item.address }}
        </li>
      </ul>
      <div class="no-results shadow" v-show="open && !hasItems">
        {{ notFoundMessage }}
      </div>
    </div>
  </div>
</template>

<script>
  import InputWidget from "../components/input-widget/InputWidget";
  import Typeahead from "../components/common/Typeahead";
  import i18n from "../../shared/i18n";
  import _ from "lodash";
  import {PostCodeHelperService} from "../services/postCodeHelper.service";

  export default {
    name: "PostCodeTypeahead",
    components: {Typeahead, InputWidget},
    props: {
      model: {
        type: Object,
        required: true
      },
      attribute: {
        type: String,
        required: true
      },
      label: {
        type: [String, Boolean],
        default: null
      },
      rules: {
        type: [String, Object],
        default: null
      },
      disabled: {
        type: Boolean,
        default: false
      },
      notFoundMessage: {
        type: String,
        default: i18n.t('No Data Found')
      }
    },
    data() {
      return {
        items: [],
        open: false,
        activeIndex: 0,
        isFocused: false,
      }
    },
    computed: {
      hasItems() {
        return this.items.length;
      },
      isOpen() {
        return this.model[this.attribute] && (this.open || this.isFocused) && this.hasItems;
      },
    },
    methods: {
      onKeyup($event) {
        this.$emit('keyup', $event);
      },
      onBlur($event) {
        this.open = false;

        this.$emit('blur', $event);
      },
      onMousedown($event) {
        if (this.items.length) {
          this.open = true;
        }

        this.$emit('mousedown', $event);
      },
      onArrowKeyDown() {
        this.activeIndex++;
        if (this.activeIndex > this.items.length) {
          this.activeIndex = 0;
        }
      },
      onArrowKeyUp() {
        this.activeIndex--;
        if (this.activeIndex < 0) {
          this.activeIndex = this.items.length;
        }
      },
      resetInfo() {
        this.open = false;
        this.isFocused = false;
        this.activeIndex = 0;
      },
      getPostCodeInfo: _.debounce(async function (value) {
        const {success, body} = await PostCodeHelperService.getInfoByPostCode({post_code: value.trim()});
        if (success) {
          this.items = _.cloneDeep(body.suggestions || []);
          this.open = true;
        } else if (body.non_field_errors) {
          this.$errorDialog(body.non_field_errors[0])
        }
      }, 500),
      async getAddressInfo($event) {
        $event.preventDefault();
        const value = this.items[this.activeIndex];
        if (value) {
          const {success, body} = await PostCodeHelperService.getInfoByAddress({address_url: value.url});
          this.resetInfo();
          if (success) {
            if (Object.keys(body).length) {
              this.$emit('onChangeAddressInfo', body);
            }
          } else if (body.non_field_errors) {
            this.$errorDialog(body.non_field_errors[0])
          }
        }
      },
      setActive(index) {
        this.activeIndex = index;
      },
    },
  }
</script>

<style lang="scss" scoped>
  .typeahead {
    position: relative;
  }

  .results-ctr {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 3;
    background: white;
  }

  .result-list {
    max-height: 160px;
    overflow: auto;
    list-style: none;
    padding: 0;

    > li {
      padding: 16px 32px;

      &.active {
        background-color: #03a9f4;
        color: white;
      }
    }
  }

  .no-results {
    padding: 10px 12px;
  }
</style>